import React, { useState, useEffect } from 'react';
import './styles/om.css';
import SecondaryButton from './secondaryButton';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const Om = () => {
  const [psykologer, setPsykologer] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [languageState, setLanguageState] = useState({});

  // Funktion til at skifte sprog for en bestemt psykolog
  const toggleLanguage = (psykologId) => {
    setLanguageState(prevState => ({
      ...prevState,
      [psykologId]: !prevState[psykologId]
    }));
  };

  useEffect(() => {
    const fetchPsykologer = async () => {
      try {
        const omDocRef = doc(db, 'content', 'om');
        const omDoc = await getDoc(omDocRef);
        
        if (omDoc.exists() && omDoc.data().psykologer) {
          setPsykologer(omDoc.data().psykologer);
          
          // Initialiser sprogtilstanden for hver psykolog med engelsk indhold
          const initialLangState = {};
          omDoc.data().psykologer.forEach(psykolog => {
            initialLangState[psykolog.id] = false; // Start med dansk
          });
          setLanguageState(initialLangState);
        } else {
          // Fallback til standard psykologer hvis data ikke findes
          setPsykologer([
            {
              id: 'maria',
              navn: 'Maria Højer Nannestad',
              titler: 'Autoriseret Psykolog, specialist i klinisk børne- og ungepsykologi samt psykoterapi med børn og med voksne. Specialistgodkendt supervisor.',
              speciale: 'Speciale i vold i nære relationer og tager udelukkende forløb med børn, unge og voksne, der har levet med vold i familien, fx i forbindelse med ophold på krisecenter.',
              email: 'maria@psykologiskperspektiv.dk',
              telefon: '23 27 65 95',
              billedeUrl: '/media/om/maria.avif',
              link: '/om/maria',
              punkter: []
            },
            {
              id: 'susanne',
              navn: 'Susanne Wederkinck Nielsen',
              titler: 'Autoriseret psykolog, specialist i klinisk børnepsykologi og psykoterapi med børn. Specialistgodkendt supervisor.',
              speciale: 'Speciale i Mentaliseringsbaseret terapi med børn, unge og familier samt supervision og undervisning i MBT.',
              email: 'susanne@psykologiskperspektiv.dk',
              telefon: '61 12 66 85',
              billedeUrl: '/media/om/susanne.avif',
              link: '/om/susanne',
              punkter: []
            },
            {
              id: 'sidsel',
              navn: 'Sidsel Linnet',
              titler: 'Autoriseret Psykolog, Registered Clinical Psychologist. På vej til at blive specialist i Klinisk børne og Ungepsykologi.',
              speciale: 'Speciale i unge.',
              email: 'sidsel@psykologiskperspektiv.dk',
              telefon: '53 85 11 75',
              billedeUrl: '/media/om/sidsel.avif',
              link: '/om/sidsel',
              punkter: [
                'engelsktalende klienter med expat-baggrund',
                'vold i nære relationer',
                'supervision og undervisning i MBT'
              ]
            }
          ]);
          
          // Initialiser sprogtilstand for standardpsykologer
          const initialLangState = {};
          ['maria', 'susanne', 'sidsel'].forEach(id => {
            initialLangState[id] = false;
          });
          setLanguageState(initialLangState);
        }
      } catch (error) {
        console.error('Fejl ved hentning af psykologer:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPsykologer();
  }, []);

  if (isLoading) {
    return <div className="pp-om-loading">Indlæser...</div>;
  }

  return (
    <section className="pp-om">
      <h2 className="pp-om__title">Om os</h2>
      <div className="pp-om__grid">
        {psykologer.map((psykolog) => (
          <article key={psykolog.id} className="pp-om__psykolog">
            <div className="pp-om__psykolog-billede">
              <img src={psykolog.billedeUrl} alt={psykolog.navn} />
            </div>
            <div className="pp-om__psykolog-indhold">
              <div className="pp-om__psykolog-header">
                <h3 className="pp-om__psykolog-navn">{psykolog.navn}</h3>
                {psykolog.engBeskrivelse && (
                  <button 
                    className={`language-toggle ${languageState[psykolog.id] ? 'english' : 'danish'}`}
                    onClick={() => toggleLanguage(psykolog.id)}
                  >
                    {languageState[psykolog.id] ? 'Dansk' : 'English'}
                  </button>
                )}
              </div>
              <div className="pp-om__psykolog-tekst">
                {!languageState[psykolog.id] || !psykolog.engBeskrivelse ? (
                  // Dansk beskrivelse
                  <>
                    <p>{psykolog.titler}</p>
                    {psykolog.speciale && (
                      <p><strong>{psykolog.speciale}</strong></p>
                    )}
                    {psykolog.punkter && psykolog.punkter.length > 0 && (
                      <>
                        <p><strong>Særlig erfaring og interesse i:</strong></p>
                        <ul>
                          {psykolog.punkter.map((punkt, index) => (
                            <li key={index}>{punkt}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </>
                ) : (
                  // Engelsk beskrivelse - kun vis titler og speciale
                  <>
                    <p>{psykolog.engTitler || psykolog.titler}</p>
                    {psykolog.engSpeciale && (
                      <p><strong>{psykolog.engSpeciale}</strong></p>
                    )}
                    {psykolog.engPunkter && psykolog.engPunkter.length > 0 && (
                      <>
                        <p><strong>Special experience and interest in:</strong></p>
                        <ul>
                          {psykolog.engPunkter.map((punkt, index) => (
                            <li key={index}>{punkt}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </>
                )}
              </div>
              <div className="pp-om__psykolog-kontakt">
                {!languageState[psykolog.id] || !psykolog.engBeskrivelse ? (
                  // Dansk kontaktinfo
                  <>
                    <p>Email: {psykolog.email}</p>
                    <p>Tlf: {psykolog.telefon}</p>
                  </>
                ) : (
                  // Engelsk kontaktinfo
                  <>
                    <p>{psykolog.engEmail || 'Email:'} {psykolog.email}</p>
                    <p>{psykolog.engTelefon || 'Phone:'} {psykolog.telefon}</p>
                  </>
                )}
              </div>
              <div className="pp-om__psykolog-button">
                <SecondaryButton href={psykolog.link}>
                  {languageState[psykolog.id] && psykolog.engBeskrivelse ? 'Read more' : 'Læs mere'}
                </SecondaryButton>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default Om;
